import { IDatasource, IGetRowsParams, SortModelItem } from '@ag-grid-community/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ResponsiveGridState } from './types';

type UseInfiniteCardDataOptions = {
    datasource: IDatasource | undefined;
    state: ResponsiveGridState;
    stateVersion: number;
    pageSize: number;
    maxConcurrentRequests?: number;
};

type UseInfiniteCardDataResult<TData> = {
    rows: TData[];
    isLoading: boolean;
    hasMore: boolean;
    error: string | undefined;
    loadMore: () => void;
    reset: () => void;
};

export function useInfiniteCardData<TData>({
    datasource,
    state,
    stateVersion,
    pageSize,
    maxConcurrentRequests = 1,
}: UseInfiniteCardDataOptions): UseInfiniteCardDataResult<TData> {
    const [rows, setRows] = useState<TData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [error, setError] = useState<string | undefined>();

    const activeRequestsRef = useRef(0);
    const nextStartRowRef = useRef(0);
    const isMountedRef = useRef(true);
    const lastVersionRef = useRef(stateVersion);
    const requestVersionRef = useRef(0);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fetchPage = useCallback(
        (startRow: number, sortModel: SortModelItem[], filterModel: Record<string, unknown>) => {
            if (!datasource || activeRequestsRef.current >= maxConcurrentRequests) {
                return;
            }

            activeRequestsRef.current += 1;
            setIsLoading(true);
            setError(undefined);

            const requestToken = requestVersionRef.current;
            const endRow = startRow + pageSize;

            const params: IGetRowsParams = {
                startRow,
                endRow,
                sortModel,
                filterModel,
                successCallback: (rowsThisBlock: unknown[], lastRow?: number) => {
                    if (!isMountedRef.current || requestToken !== requestVersionRef.current) {
                        return;
                    }
                    activeRequestsRef.current -= 1;

                    const typedRows = rowsThisBlock as TData[];

                    setRows((prev) => {
                        if (startRow === 0) {
                            return typedRows;
                        }
                        return [...prev, ...typedRows];
                    });

                    nextStartRowRef.current = startRow + typedRows.length;

                    if (lastRow !== undefined && lastRow !== -1) {
                        setHasMore(nextStartRowRef.current < lastRow);
                    } else {
                        setHasMore(typedRows.length >= pageSize);
                    }

                    setIsLoading(activeRequestsRef.current > 0);
                },
                failCallback: () => {
                    if (!isMountedRef.current || requestToken !== requestVersionRef.current) {
                        return;
                    }
                    activeRequestsRef.current -= 1;
                    setError('Failed to load data');
                    setIsLoading(activeRequestsRef.current > 0);
                },
                context: undefined,
            };

            try {
                datasource.getRows(params);
            } catch {
                activeRequestsRef.current -= 1;
                setIsLoading(activeRequestsRef.current > 0);
                setError('Failed to load data');
            }
        },
        [datasource, pageSize, maxConcurrentRequests],
    );

    const reset = useCallback(() => {
        requestVersionRef.current += 1;
        setRows([]);
        setHasMore(true);
        setError(undefined);
        setIsLoading(false);
        activeRequestsRef.current = 0;
        nextStartRowRef.current = 0;
    }, []);

    // Reset and fetch first page when state version changes (filter/sort changed)
    useEffect(() => {
        if (lastVersionRef.current !== stateVersion) {
            lastVersionRef.current = stateVersion;
            reset();
        }
    }, [stateVersion, reset]);

    // Fetch first page on mount or after reset
    useEffect(() => {
        if (!datasource) {
            return;
        }

        if (rows.length === 0 && !isLoading && hasMore && !error) {
            fetchPage(0, state.sortModel, state.filterModel);
        }
    }, [datasource, rows.length, isLoading, hasMore, error, fetchPage, state.sortModel, state.filterModel]);

    const loadMore = useCallback(() => {
        if (!isLoading && hasMore && !error && datasource) {
            fetchPage(nextStartRowRef.current, state.sortModel, state.filterModel);
        }
    }, [isLoading, hasMore, error, datasource, fetchPage, state.sortModel, state.filterModel]);

    return {
        rows,
        isLoading,
        hasMore,
        error,
        loadMore,
        reset,
    };
}
